import React, { useState, useEffect } from 'react';
import P, { InferProps } from 'prop-types';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { SxStyle } from '../../../types';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { Avatar, ListItemAvatar, ListItemIcon, ListItemText } from '@mui/material';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import { getAppConfigPath } from '../../../utils/paths';
import { PROMO_VARIANT } from '../../../types/team';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { doc } from 'firebase/firestore';
import FormHelperText from '@mui/material/FormHelperText';
import { Promo } from '../../../types/app-config';

const MAX_SELECTABLE_PROMOS = 3;

const styles: SxStyle = {
  formControl: {
    minWidth: 214,
  },
  timedIcon: {
    paddingLeft: '4px',
  },
};

function PromoSelectFieldMultiple({
  field,
  setChange,
  isReadOnly,
  path,
}: InferProps<typeof PromoSelectFieldMultiple.propTypes>) {
  const [value, setValue] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const { strings } = useStringsContext();
  const [errorMessage, setErrorMessage] = useState<string>('');

  // TODO Use promos from props (see timed promos)
  const [appConfig, loading, error] = useDocumentData(doc(firebaseApp.db, getAppConfigPath(path)));

  const promos: Promo[] = appConfig?.promos;

  useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    // Check if all promos have the same ratio, if not, show error message
    if (promos && value.length > 0) {
      const selectedPromos = value.map((promoIndex) => {
        return promos[parseInt(promoIndex, 10)];
      });

      let previousRatio = 0;
      const sameRatio = selectedPromos.every((promo, index) => {
        if (promo?.height && promo?.width) {
          const promoRatio = promo.height / promo.width;
          if (index > 0 && promoRatio !== previousRatio) {
            return false;
          }
          previousRatio = promoRatio;
        }
        return true;
      });

      setErrorMessage(!sameRatio ? strings.messages.promoRatioError : '');
    }
  }, [promos, strings, value]);

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    const inputValue = e.target.value;
    if (Array.isArray(inputValue) && inputValue.length <= MAX_SELECTABLE_PROMOS) {
      if (setChange) {
        setChange(field.key, inputValue, field.validation);
      }
    }
  };

  if (loading || error) {
    return (
      <FormControl variant="outlined" sx={styles.formControl}>
        <InputLabel id={`${field.id}-label`} htmlFor={field.id}>
          {field.title}
        </InputLabel>
        <Select
          id={field.id}
          labelId={`${field.id}-label`}
          disabled={!!isReadOnly}
          value=""
          label={field.title}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
        >
          <MenuItem value="">
            <em>{error ? strings.messages.error : strings.messages.loading}</em>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  const renderValue = (value: string[]) => {
    return `Promo ${value.join(', ')}`;
  };

  return (
    <FormControl variant="outlined" sx={styles.formControl}>
      <InputLabel id={`${field.id}-label`} htmlFor={field.id}>
        {field.title}
      </InputLabel>
      <Select
        disabled={!!isReadOnly}
        labelId={`${field.id}-label`}
        id={field.id}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        value={value}
        onChange={handleChange}
        renderValue={renderValue}
        color="primary"
        label={field.title}
        multiple
      >
        {field.options
          .filter((option: string | number | null) =>
            field.optionsFilter
              ? promos[option as number]?.promo_type === field.optionsFilter
              : true
          )
          .map((option: string | number | null) => {
            const promo = promos[option as number];
            const promoText = `Promo ${option as string}`;
            return (
              <MenuItem value={option as string} key={`${field.id}-${option}`}>
                <ListItemIcon>
                  <ListItemAvatar>
                    {promo?.promo_type === PROMO_VARIANT.FORZASYS_VIDEO ? (
                      <Avatar variant="rounded">
                        <PlayCircleOutlineIcon />
                      </Avatar>
                    ) : (
                      <Avatar alt={promoText} src={promo?.contentUrl || ''} variant="rounded" />
                    )}
                  </ListItemAvatar>
                </ListItemIcon>
                {promo ? (
                  <ListItemText primary={promoText} />
                ) : (
                  <ListItemText primary={`${promoText} (${strings.promos.promoEmpty})`} />
                )}
              </MenuItem>
            );
          })}
      </Select>
      <FormHelperText error>{errorMessage}</FormHelperText>
    </FormControl>
  );
}

PromoSelectFieldMultiple.propTypes = {
  setChange: P.func,
  isReadOnly: P.bool,
  path: P.string.isRequired,
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any,
    key: P.string,
    optionsFilter: P.string,
    options: P.arrayOf(P.oneOfType([P.string, P.number]).isRequired).isRequired,
    value: P.arrayOf(P.string.isRequired).isRequired,
  }).isRequired,
};

export default PromoSelectFieldMultiple;
